import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { AppCard, AppRemixIcon, AppNoInfo } from '@/layout/AppComponents';
import { Accordion } from 'react-bootstrap';
import ReactDOMServer from 'react-dom/server';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath } from '../../../routing/helpers/router';
import * as moduleAction from '../../../store/actions/moduleAction';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import Translator from '../../../layout/components/helpers/Translator';
import Sidebar from '../../../layout/Sidebar/Sidebar';
import AppLink from '../../../routing/helpers/AppLink';
import PlantEvidence from '../../../layout/screenSpecifics/Modules/LegalCompliance/Evaluation/PlantInformation/PlantEvidence';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';

const LegalCompliancePlantInformationScreen = () => {
  const dispatch = useDispatch();
  const { moduleBridgeHex, plantHex } = useParams();
  const isMounted = useIsMounted();
  const auth = useSelector((state) => state.auth);
  const permissions = useSelector(
    (state) => state.module[moduleBridgeHex]?.permissions
  );
  const userBridgeSettings = useSelector(
    (s) => s.module?.[moduleBridgeHex]?.userBridge?.permissions
  );
  const evidencesPermissions = useSelector(
    (s) =>
      s.module?.[moduleBridgeHex]?.permissions?.gcl?.module?.plant?.evidences
  );
  const [layout, setLayout] = useState();
  const [plantInfo, setPlantInfo] = useState();
  const [hasReadEvidencePermission, setHasReadEvidencePermission] = useState(
    false
  );

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  useEffect(() => {
    if (evidencesPermissions && userBridgeSettings) {
      setHasReadEvidencePermission(
        !!evidencesPermissions &&
          (userBridgeSettings.level !== 2 ||
            (userBridgeSettings.level === 2 &&
              userBridgeSettings.evidences.read))
      );
    }
  }, [evidencesPermissions, userBridgeSettings]);

  // < STORE CALLS
  const loadModulePermissions = useCallback(async () => {
    try {
      await dispatch(
        moduleAction.fetch(
          axiosPath(
            "api_r_user_module_legalCompliance_evaluation_getPermissions"
          ),
          "permissions",
          {
            moduleBridgeHex,
            plantHex,
            subType: "permissions",
          }
        )
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath(
            "api_r_user_module_legalCompliance_evaluation_getPermissions"
          ),
          "userBridge",
          {
            moduleBridgeHex,
            plantHex,
            subType: "userBridge",
          }
        )
      );
    } catch (err) {}
  }, [moduleBridgeHex, plantHex]);

  useEffect(() => {
    loadModulePermissions();
  }, [moduleBridgeHex, plantHex]);
  // < STORE CALLS

  // > LOAD PLANT INFORMATION
  useEffect(() => {
    (async () => {
      try {
        const response = await axios.post(
          axiosPath("api_r_user_module_legalCompliance_plantInformation_get"),
          { moduleBridgeHex, plantHex }
        );
        if (isMounted.current) {
          setPlantInfo(response.data);
          if (response.data.evidenceFolderList)
            setEvidenceFolders(response.data.evidenceFolderList);
        }
      } catch (err) {}
    })();
  }, []);
  // < LOAD PLANT INFORMATION

  // > LOAD LAYOUT
  useEffect(() => {
    if (moduleBridgeHex && plantHex) {
      (async () => {
        try {
          const response = await axios.post(
            axiosPath(
              "api_r_user_module_legalCompliance_evaluation_loadLayout"
            ),
            { moduleBridgeHex, plantHex }
          );
          if (isMounted.current) {
            setLayout(response.data);
            dispatch(
              currentPageAction.change("options", response.data.options)
            );
          }
        } catch (err) {}
      })();
    }
  }, [moduleBridgeHex, plantHex]);
  // < LOAD LAYOUT

  return (
    <div>
      <Sidebar>
        <img
          src={auth.user?.client?.logoUrl}
          className="img-fluid"
          alt={auth.user?.client?.name}
          title={auth.user?.client?.name}
        />
        <Accordion>
          <ul>
            <li>
              <AppLink
                path="user_module_legalCompliance_evaluation"
                params={{
                  moduleBridgeHex,
                  plantHex,
                  page: 0,
                  filterHex: 0,
                }}
                className="sidebar-element"
              >
                <AppRemixIcon
                  icon="arrow-go-back-line"
                  className="sidebar-icon"
                  noVariants
                />{' '}
                <span className="sb-title">
                  <Translator file="module-legalCompliance" name="evaluation" />
                </span>
              </AppLink>
            </li>
          </ul>
        </Accordion>
      </Sidebar>
      <BreadcrumbHandler
        paths={
          layout
            ? [
                {
                  name: layout.plant.name,
                  route: {
                    name: 'user_module_legalCompliance_evaluation',
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 0,
                      filterHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator file="user" name="user.info" />
                  ),
                },
              ]
            : [{ name: '...' }]
        }
      />
      {plantInfo ? (
        <main role="main" className="col-md-12 col-lg-12 px-md-4">
          {plantInfo.description && (
            <AppCard
              header={
                <Translator
                  file="module-legalCompliance"
                  name="module.lc.plant-info.description"
                />
              }
              className="mb-2"
            >
              <div
                dangerouslySetInnerHTML={{ __html: plantInfo.description }}
              />
            </AppCard>
          )}
          {permissions?.gcl?.module?.plant?.plantInfo?.userList &&
            plantInfo.userList && (
              <AppCard
                header={
                  <Translator
                    file="module-legalCompliance"
                    name="module.lc.plant-info.user-list"
                  />
                }
                className="mb-2"
              >
                <table className="table">
                  <thead>
                    <tr>
                      <th>
                        <Translator
                          file="user"
                          name="user.password.forgotten.remember.name"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {plantInfo.userList.map((userName, index) => (
                      <tr key={index}>
                        <td>{userName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {plantInfo.userList.length === 0 && (
                  <div className="alert alert-info" role="alert">
                    <Translator
                      file="module-legalCompliance"
                      name="module.lc.plant-info.user-list.no-result"
                    />
                  </div>
                )}
              </AppCard>
            )}
          {permissions?.gcl?.module?.plant?.plantInfo?.noteList &&
            plantInfo.noteList && (
              <AppCard
                header={<Translator file="filter" name="filter.note" />}
                className="mb-2"
              >
                <table className="table">
                  <tbody>
                    {plantInfo.noteList.map((noteName, index) => (
                      <tr key={index}>
                        <td>{noteName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {plantInfo.noteList.length === 0 && (
                  <div className="alert alert-info" role="alert">
                    <Translator
                      file="module-legalCompliance'"
                      name="module.lc.plant-info.user-list.no-result"
                    />
                  </div>
                )}
              </AppCard>
            )}
          {permissions?.gcl?.module?.plant?.plantInfo?.categoryList &&
            plantInfo.categoryList && (
              <AppCard
                header={<Translator file="filter" name="filter.category" />}
                className="mb-2"
              >
                <table className="table">
                  <tbody>
                    {plantInfo.categoryList.map((categoryName, index) => (
                      <tr key={index}>
                        <td>{categoryName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {plantInfo.categoryList.length === 0 && (
                  <div className="alert alert-info" role="alert">
                    <Translator
                      file="module-legalCompliance'"
                      name="module.lc.plant-info.user-list.no-result"
                    />
                  </div>
                )}
              </AppCard>
            )}
          {permissions?.gcl?.module?.plant?.plantInfo?.evidences &&
            hasReadEvidencePermission &&
            plantInfo.evidenceFolderList.length > 0 && (
              <AppCard
                header={
                  <Translator
                    file="module-legalCompliance"
                    name="user.obligation.plant-evidence.list"
                  />
                }
                className="mb-2"
              >
                <PlantEvidence
                  evidenceFolderList={plantInfo.evidenceFolderList}
                />
              </AppCard>
            )}
        </main>
      ) : (
        <AppNoInfo />
      )}
    </div>
  );
};

export default LegalCompliancePlantInformationScreen;
